import React, { useEffect, useCallback, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { selectAPISession } from "../store/sessionSlice";
import { WebSocketConnection } from "../utils/WebSocketConnection";
import { KeyType, MicrophoneImpl } from "../utils/MicrophoneImpl";
import { AudioMode, AudioSender } from "../utils/audioUtils";
import { setDesiredEarMode } from "../store/audioSettingsSlice";
import { AudioMessage_EarMode, AudioMessage_ClientPauseStatus } from "../generated/audio_message";
import { DisplayMode } from "./PixelStreamingWrapper";
import { updateClientInfoState } from "../utils/protobuf_utils";
import { useMicrophone } from "../hooks/useMicrophone";
import MicButton from "./useroverlay/MicButton";
import MicModeToggle from "./useroverlay/MicModeToggle";
import PlayPauseButton from "./useroverlay/PlayPauseButton";
import MobileTouchOverlay from "./useroverlay/MobileTouchOverlay";
import { PermissionOverlay } from "./MicrophonePermissions";
import WaveformDisplay from "./WaveformDisplay";
import GuiPopupBubbleContainer from "./hud/GuiPopupBubbleContainer";
import SidePanel, { SidePanelMode } from "./sidepanel/SidePanel";
import config from "../utils/config";
import { getGUIThemeProperties } from "../utils/baseConfig";
import { selectGUITheme } from "../store/userSettingsSlice";

import settingsIcon from "../assets/hamburger.svg";
import feedbackIcon from "../assets/feedback.svg";
import "../styles/components/UserOverlay.css";
import "../styles/components/UserOverlayLayout.css";
import "../styles/components/useroverlay/theme-original.css";
import "../styles/components/useroverlay/theme-arcade.css";
import "../styles/components/useroverlay/theme-complex1.css";
import "../styles/components/useroverlay/theme-complex2.css";
import "../styles/global.css";

interface UserOverlayProps {
  connection: WebSocketConnection | null;
  displayMode?: DisplayMode;
  setDisplayMode?: (displayMode: DisplayMode) => void;
  onAudioSpeakerChange: (deviceId: string) => void;
  onAudioSpeakerMuteChange: (muted: boolean) => void;
  audioSpeakerMuteGetter: () => boolean;
  onAudioSpeakerVolumeChange: (volume: number) => void;
  audioSpeakerVolumeGetter: () => number;
}

const UserOverlay: React.FC<UserOverlayProps> = ({
  connection,
  displayMode = "fit",
  setDisplayMode,
  onAudioSpeakerChange,
  onAudioSpeakerMuteChange,
  audioSpeakerMuteGetter,
  onAudioSpeakerVolumeChange,
  audioSpeakerVolumeGetter,
}) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const dispatch = useDispatch();

  const [showAudioSettings, setShowAudioSettings] = useState(false);
  const [isMouseInWindow, setIsMouseInWindow] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [audioMode, setAudioMode] = useState<AudioMode>(params.get('openMic') === 'true' ? "open-mic" : "push-to-talk");
  const [settingsShowFeedback, setSettingsShowFeedback] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);

  const settingsButtonRef = useRef<HTMLDivElement>(null);
  const hideButtonsTimer = useRef<NodeJS.Timeout | null>(null);
  const audioSenderRef = useRef<AudioSender>(new AudioSender());

  const apiSession = useSelector(selectAPISession);
  const guiTheme = useSelector(selectGUITheme);
  const styleProps = getGUIThemeProperties(guiTheme);
  const showMicIcon = styleProps.showMicIcon;

  function handleMicrophoneActivated() {
    audioSenderRef.current.startNextCluster();
  }

  function handleMicrophoneDeactivating() {
    audioSenderRef.current.setIsDeactivating(true);
  }

  // Use the microphone hook for all mic-related logic
  const micState = useMicrophone({
    onMicrophoneActivated: handleMicrophoneActivated,
    onMicrophoneDeactivating: handleMicrophoneDeactivating,
    audioMode,
    connection,
  });

  const handleAudioModeChange = useCallback((mode: AudioMode) => {
    console.log("[UserOverlay] Audio mode changed to:", mode);
    setAudioMode(mode);
    const earMode =
      mode === "push-to-talk"
        ? AudioMessage_EarMode.PUSH_TO_TALK
        : AudioMessage_EarMode.CONVERSATION;
    dispatch(setDesiredEarMode(earMode));
    audioSenderRef.current.startNextCluster();
  }, [dispatch]);

  const handleMicModeToggle = useCallback((mode: AudioMode) => {
    const earMode =
      mode === "push-to-talk"
        ? AudioMessage_EarMode.PUSH_TO_TALK
        : AudioMessage_EarMode.CONVERSATION;
    dispatch(setDesiredEarMode(earMode));
  }, [dispatch]);

  useEffect(() => {
    handleAudioModeChange(audioMode);
  }, []);

  const handleAudioSpeakerChanged = useCallback(
    (deviceId: string) => {
      onAudioSpeakerChange(deviceId);
    },
    [onAudioSpeakerChange]
  );

  const handleAudioSpeakerMuteChanged = useCallback(
    (muted: boolean) => {
      onAudioSpeakerMuteChange(muted);
    },
    [onAudioSpeakerMuteChange]
  );

  const getAudioSpeakerMuted = useCallback(() => {
    return audioSpeakerMuteGetter();
  }, [audioSpeakerMuteGetter]);

  const handleAudioSpeakerVolumeChanged = useCallback(
    (volume: number) => {
      onAudioSpeakerVolumeChange(volume);
    },
    [onAudioSpeakerVolumeChange]
  );

  const getAudioSpeakerVolume = useCallback(() => {
    return audioSpeakerVolumeGetter();
  }, [audioSpeakerVolumeGetter]);

  const handleWaveformData = useCallback(
    (data: Int16Array, isFirst: Boolean, isLast: Boolean, keyType: KeyType) => {
        // ATM we force 16khz, so just hardcode the rate.
        audioSenderRef.current.sendAudioData(data, isFirst, isLast, keyType, 16000);
    },
    []
  );

  // Set up microphone waveform listener.
  useEffect(() => {
    const microphoneImpl = MicrophoneImpl.getInstance();
    microphoneImpl.addWaveformCallback("useroverlay", handleWaveformData);

    return () => {
      microphoneImpl.removeWaveformCallback("useroverlay");
    };
  }, [handleWaveformData]);

  const handleSettingsClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      setSettingsShowFeedback(false);
      setShowAudioSettings(!showAudioSettings);
    },
    [showAudioSettings]
  );

  const handleFeedbackClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setSettingsShowFeedback(true);
      setShowAudioSettings(!showAudioSettings);
    },
    [showAudioSettings]
  );

  const handleMouseEnter = useCallback(() => {
    setIsMouseInWindow(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsMouseInWindow(false);
    setShowButtons(false);
    if (hideButtonsTimer.current) {
      clearTimeout(hideButtonsTimer.current);
      hideButtonsTimer.current = null;
    }
  }, []);

  const handleMouseMove = useCallback(() => {
    if (isMouseInWindow) {
      setShowButtons(true);
      if (hideButtonsTimer.current) {
        clearTimeout(hideButtonsTimer.current);
      }
      hideButtonsTimer.current = setTimeout(() => {
        setShowButtons(false);
        hideButtonsTimer.current = null;
      }, 5000);
    }
  }, [isMouseInWindow]);

  useEffect(() => {
    return () => {
      if (hideButtonsTimer.current) {
        clearTimeout(hideButtonsTimer.current);
      }
    };
  }, []);

  // Handle play/pause toggle
  const handlePlayPauseToggle = useCallback(async () => {
    const newIsPlaying = !isPlaying;
    setIsPlaying(newIsPlaying);

    // If we are currently in open-mic, deactivate/activate mic accordingly
    if (audioMode === "open-mic") {
      if (newIsPlaying) {
        await micState.activateMicrophone(KeyType.SPACE);
      } else {
        micState.deactivateMicrophone();
      }
    }
    
    // Update client pause status
    if (newIsPlaying) {
      updateClientInfoState({ userPauseStatus: AudioMessage_ClientPauseStatus.PLAY });
    } else {
      updateClientInfoState({ userPauseStatus: AudioMessage_ClientPauseStatus.PAUSE_SYLLABLE });
    }
  }, [isPlaying, audioMode, micState.activateMicrophone, micState.deactivateMicrophone]);

  // Waveform mode: overlay or replace (from behavioral config)
  const showWaveformOverlay = styleProps.waveformMode === 'overlay' && micState.displayState.showWaveform;
  const showWaveformReplace = styleProps.waveformMode === 'replace' && micState.displayState.showWaveform;
  const showMicButton = !isPlaying || styleProps.waveformMode === 'overlay' || !micState.displayState.showWaveform;

  return (
    <div
      className={`user-overlay theme-${guiTheme}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      {/* Blur overlay when paused */}
      {!isPlaying && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Permission overlay */}
      {micState.showPermissionOverlay && <PermissionOverlay />}

      {/* Microphone UI - only show if allowed */}
      {!micState.pttHidden && (
        <>
          {/* Mic Button with optional waveform overlay */}
          {showMicButton && (
            <div className="mic-position-bottom-center">
              <div className="waveform-wrapper">
                {(showWaveformOverlay && isPlaying) && (
                  <div className="inline-waveform">
                    <WaveformDisplay />
                  </div>
                )}
                
                <MicButton
                  active={micState.active}
                  isWindowFocused={micState.isWindowFocused}
                  visible={micState.displayState.visible}
                  hasEverTransmitted={micState.hasEverTransmitted}
                  isTalkingButNotTransmitting={micState.isTalkingButNotTransmitting}
                  text={!isPlaying ? "Conversation paused" : (micState.displayState.text ?? undefined)}
                  micColor={micState.micColor ?? undefined}
                  showIcon={showMicIcon}
                />
              </div>       
            </div>
          )}

          {/* Waveform in replace mode */}
          {(showWaveformReplace && isPlaying) && (
            <div className="mic-position-bottom-center">
              <div className="waveform-wrapper">
                <div className="inline-waveform">
                  <WaveformDisplay />
                </div>
              </div>
            </div>
          )}

          {/* Control buttons container */}
          {(micState.showLiveMicToggle || styleProps.showPlayPause) && (
            <div className="controls-position-bottom-left controls-grouped">
              {micState.showLiveMicToggle && (
                <MicModeToggle
                  onChange={(checked) =>
                    handleMicModeToggle(checked ? "open-mic" : "push-to-talk")
                  }
                  variant={styleProps.micModeToggleVariant}
                />
              )}
              {styleProps.showPlayPause && (
                <PlayPauseButton
                  isPlaying={isPlaying}
                  onToggle={handlePlayPauseToggle}
                />
              )}
            </div>
          )}

          {/* Mobile Touch Overlay */}
          <MobileTouchOverlay
            onTouchStart={micState.handleTouchStart}
            onTouchEnd={micState.handleTouchEnd}
            settingsOpen={showAudioSettings}
          />
        </>
      )}

      {/* Settings and Feedback Buttons */}
      {(config.enableSidePanel && !showAudioSettings) && (
        <div className="settings-buttons-top-right">
          {config.enableStandardFeedbackButton && (
            <div
              className={`settings-button feedback ${showButtons ? "visible" : "hidden"}`}
              onClick={(e) => handleFeedbackClick(e)}
              title="Feedback"
            >
              <img src={feedbackIcon} alt="Feedback" />
            </div>
          )}
          <div
            ref={settingsButtonRef}
            className={`settings-button ${showButtons ? "visible" : "hidden"}`}
            onClick={(e) => handleSettingsClick(e)}
            title="Settings"
          >
            <img src={settingsIcon} alt="Settings" />
          </div>
        </div>
      )}

      {/* GUI Popup Bubbles */}
      <GuiPopupBubbleContainer connection={connection} />

      {/* Side Panel */}
      {(config.enableSidePanel && showAudioSettings) && (
        <SidePanel
          mode={settingsShowFeedback ? SidePanelMode.Feedback : SidePanelMode.Settings}
          onClose={() => setShowAudioSettings(false)}
          triggerRef={settingsButtonRef}
          connection={connection}
          onAudioSpeakerChange={handleAudioSpeakerChanged}
          onAudioSpeakerMuteChange={handleAudioSpeakerMuteChanged}
          audioSpeakerMuteGetter={getAudioSpeakerMuted}
          onAudioSpeakerVolumeChange={handleAudioSpeakerVolumeChanged}
          audioSpeakerVolumeGetter={getAudioSpeakerVolume}
          setDisplayMode={setDisplayMode}
          displayMode={displayMode}
          setAudioMode={handleAudioModeChange}
          audioMode={audioMode}
          conversationId={apiSession?.conversation_id}
          sessionId={apiSession?.session_id}
          botName={apiSession?.pixel_streaming_experience?.bot_name}
        />
      )}
    </div>
  );
};

export default UserOverlay;
